import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import {
  useSuppliers,
  useCreateSupplier,
  useDeleteSupplier,
  useSyncSupplier,
  useProbeSupplierInline,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import { formatUsdt, relativeTime } from '@/lib/utils';
import {
  SupplierForm,
  emptySupplierForm,
  formToPayload,
  formToApiConfig,
  type SupplierFormValue,
} from '@/components/SupplierForm';
import type { SupplierApiConfigInput } from '@aggregator/shared';

function SyncButton({ id }: { id: string }) {
  const sync = useSyncSupplier(id);
  const { toast } = useToast();
  return (
    <Button
      variant="ghost"
      size="sm"
      loading={sync.isPending}
      onClick={() =>
        sync.mutate(undefined, {
          onSuccess: (r) =>
            toast({
              title: r.ok ? `Synced ${r.supplierName}` : `Sync failed`,
              description: r.ok ? `${r.fetched} fetched · +${r.created} new` : r.error,
              variant: r.ok ? 'success' : 'error',
            }),
          onError: (e) => toast({ title: 'Sync failed', description: errorMessage(e), variant: 'error' }),
        })
      }
    >
      <RefreshCw className="h-4 w-4" /> Sync
    </Button>
  );
}

export function SuppliersPage() {
  const suppliers = useSuppliers();
  const create = useCreateSupplier();
  const del = useDeleteSupplier();
  const probe = useProbeSupplierInline();
  const { toast } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<SupplierFormValue>(emptySupplierForm());

  async function fetchSample() {
    if (!form.apiConfig.baseUrl) {
      toast({ title: 'Enter the API base URL first', variant: 'error' });
      throw new Error('Base URL is required');
    }
    return probe.mutateAsync({
      apiConfig: formToApiConfig(form) as unknown as SupplierApiConfigInput,
      fieldMappings: form.fieldMappings,
    });
  }

  function submitCreate() {
    create.mutate(formToPayload(form, 'create'), {
      onSuccess: () => {
        toast({ title: 'Supplier created', variant: 'success' });
        setAddOpen(false);
        setForm(emptySupplierForm());
      },
      onError: (e) => toast({ title: 'Could not create supplier', description: errorMessage(e), variant: 'error' }),
    });
  }

  return (
    <div>
      <PageHeader
        title="Suppliers"
        description="Connect supplier APIs and keep inventory in sync."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add Supplier
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {suppliers.isLoading ? (
            <Loading />
          ) : suppliers.isError ? (
            <ErrorState error={suppliers.error} onRetry={() => suppliers.refetch()} />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Status</TH>
                  <TH>Wallet</TH>
                  <TH>Products</TH>
                  <TH>Last sync</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {suppliers.data && suppliers.data.length > 0 ? (
                  suppliers.data.map((s) => (
                    <TR key={s.id}>
                      <TD>
                        <Link className="font-medium hover:underline" to={`/suppliers/${s.id}`}>
                          {s.name}
                        </Link>
                        <div className="text-xs text-muted-foreground">{s.slug}</div>
                      </TD>
                      <TD>
                        <Badge variant={s.isActive ? 'success' : 'secondary'}>
                          {s.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {s.lastSyncError ? (
                          <Badge variant="destructive" className="ml-1">
                            sync error
                          </Badge>
                        ) : null}
                      </TD>
                      <TD>{s.walletBalance == null ? '—' : `${formatUsdt(s.walletBalance)} USDT`}</TD>
                      <TD>{s.productCount}</TD>
                      <TD className="text-muted-foreground">{relativeTime(s.lastSyncedAt)}</TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          <SyncButton id={s.id} />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (!confirm(`Delete supplier "${s.name}" and every product linked to it? This cannot be undone.`)) return;
                              del.mutate(s.id, {
                                onSuccess: (result) =>
                                  toast({
                                    title: 'Supplier deleted',
                                    description: result.deletedProducts > 0 ? `${result.deletedProducts} linked product(s) were also removed.` : undefined,
                                    variant: 'success',
                                  }),
                                onError: (e) =>
                                  toast({ title: 'Delete failed', description: errorMessage(e), variant: 'error' }),
                              });
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))
                ) : (
                  <EmptyRow colSpan={6}>No suppliers yet. Add one to get started.</EmptyRow>
                )}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add supplier"
        description="Fetch a sample response and auto-map fields, or add mappings later."
        wide
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={submitCreate}>
              Create supplier
            </Button>
          </>
        }
      >
        <SupplierForm value={form} onChange={setForm} mode="create" onFetchSample={fetchSample} />
      </Dialog>
    </div>
  );
}
